"use client";

import * as React from "react";
import { Button, TextInput, LoadingOverlay, Text, Group, Stack, Title, Box, useMantineColorScheme } from "@mantine/core";

interface MFAFormProps {
    onVerify: (code: string) => Promise<void>;
    onCancel?: () => void;
}

export function MFAForm({ onVerify, onCancel }: MFAFormProps) {
    const { colorScheme } = useMantineColorScheme();
    const [code, setCode] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            await onVerify(code);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "Invalid MFA code. Please try again."
            );
            setIsLoading(false);
        }
    };

    return (
        <Box
            p={30}
            w="100%"
            maw={400}
            style={{
                backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
                borderRadius: 'var(--mantine-radius-md)',
                border: '1px solid var(--modal-border)',
                backdropFilter: colorScheme === 'light' ? 'blur(10px)' : undefined,
                boxShadow: 'var(--mantine-shadow-md)',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <Box
                mx="-var(--mantine-spacing-xl)"
                mt="-var(--mantine-spacing-xl)"
                mb="md"
                py="xs"
                px="md"
                style={{
                    borderBottom: '1px solid var(--modal-border)',
                    backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.05)'
                }}
            >
                <Title order={3}>Two-Factor Authentication</Title>
                <Text size="sm" c={colorScheme === 'dark' ? 'gray.5' : 'gray.7'}>
                    Enter the 6-digit code from your authenticator app to complete the login.
                </Text>
            </Box>
            <form onSubmit={handleSubmit}>
                <Stack gap="md" mt="md">
                    <LoadingOverlay visible={isLoading} />
                    <TextInput
                        label="Verification Code"
                        placeholder="000000"
                        value={code}
                        onChange={(e) => setCode(e.currentTarget.value)}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        required
                        autoFocus
                        styles={{ input: { textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.5rem' } }}
                    />

                    {error && (
                        <Text c="red" size="sm">{error}</Text>
                    )}

                    <Group justify="space-between" mt="md">
                        {onCancel && (
                            <Button variant="subtle" onClick={onCancel} disabled={isLoading}>
                                Cancel
                            </Button>
                        )}
                        <Button type="submit" disabled={isLoading || code.length !== 6} fullWidth={!onCancel} color="black" c="white">
                            {isLoading ? "Verifying..." : "Verify"}
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Box>
    );
}
